import { z } from "zod";
import { resourceAccessSchema } from "./resource-references.js";
export function gmailDraftWebHref(draftId) {
    const normalizedDraftId = draftId.trim();
    if (!normalizedDraftId) {
        throw new Error("gmailDraftWebHref requires a Gmail draft id");
    }
    return `https://mail.google.com/mail/u/0/#drafts/${encodeURIComponent(normalizedDraftId)}`;
}
export const artifactProviderSchema = z.enum([
    "mlx",
    "tallie-editor-document",
    "google-doc",
    "google-sheet",
    "gmail-draft",
    "source-action",
]);
export const artifactKindSchema = z.enum([
    "markdown",
    "csv",
    /** A canvas: a self-contained page the Twin designs (see canvas.ts). */
    "html",
    "external-resource",
]);
export const artifactOperationSchema = z.enum(["created", "updated"]);
export const artifactVersionStatusSchema = z.enum([
    "accepted",
    "proposed",
    "rejected",
]);
export const artifactSummarySchema = z.object({
    id: z.string().trim().min(1),
    taskId: z.string().trim().min(1),
    runId: z.string().trim().min(1),
    sessionId: z.string().trim().min(1).optional(),
    provider: artifactProviderSchema,
    kind: artifactKindSchema,
    externalId: z.string().trim().min(1),
    title: z.string().trim().min(1),
    href: z.string().trim().min(1).optional(),
    currentVersionId: z.string().trim().min(1).optional(),
    version: z.number().int().positive(),
    operation: artifactOperationSchema.optional(),
    pendingVersionId: z.string().trim().min(1).optional(),
    pendingVersion: z.number().int().positive().optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const channelArtifactOriginSchema = z.enum(["channel", "session", "attachment"]);
export const channelArtifactSummarySchema = artifactSummarySchema.extend({
    channelId: z.string().trim().min(1),
    origins: z.array(channelArtifactOriginSchema).min(1),
    referencedSessionIds: z.array(z.string().trim().min(1)).default([]),
    /** Who can open it; members see it, guests do not. */
    access: resourceAccessSchema.optional(),
});
export const listChannelArtifactsParamsSchema = z.object({
    channelId: z.string().trim().min(1),
    cursor: z.string().trim().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const listChannelArtifactsResponseSchema = z.object({
    artifacts: z.array(channelArtifactSummarySchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
export const artifactVersionSchema = z.object({
    id: z.string().trim().min(1),
    artifactId: z.string().trim().min(1),
    version: z.number().int().positive(),
    operation: artifactOperationSchema.optional(),
    title: z.string().trim().min(1),
    href: z.string().trim().min(1).optional(),
    metadata: z.record(z.string(), z.unknown()).default({}),
    status: artifactVersionStatusSchema.default("accepted"),
    baseVersionId: z.string().trim().min(1).optional(),
    sourceRunId: z.string().trim().min(1),
    sourceSessionId: z.string().trim().min(1).optional(),
    sourceEventId: z.string().trim().min(1),
    sourceToolName: z.string().trim().min(1).optional(),
    sourceToolCallId: z.string().trim().min(1).optional(),
    createdAt: z.number(),
});
export const listArtifactsResponseSchema = z.object({
    artifacts: z.array(artifactSummarySchema),
});
/*
 * Every artifact the caller may see in one organisation, newest first, with
 * enough provenance to say where it came from: the channel that owns its task
 * (or the first channel its session linked to) and its session's title.
 * Artifacts from channel-less sessions have no channel and still appear.
 */
export const organisationArtifactSummarySchema = artifactSummarySchema.extend({
    channelId: z.string().trim().min(1).nullable(),
    sessionTitle: z.string().trim().min(1).nullable(),
});
export const listOrganisationArtifactsParamsSchema = z.object({
    artifactId: z.string().trim().min(1).max(200).optional(),
    organisationId: z.string().trim().min(1).optional(),
    cursor: z.string().trim().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
});
export const listOrganisationArtifactsResponseSchema = z.object({
    artifacts: z.array(organisationArtifactSummarySchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
export const getArtifactResponseSchema = z.object({
    artifact: artifactSummarySchema.nullable(),
    versions: z.array(artifactVersionSchema).default([]),
});
export const getArtifactParamsSchema = z.object({
    artifactId: z.string().trim().min(1),
});
export const getArtifactContentParamsSchema = z.object({
    artifactId: z.string().trim().min(1),
    versionId: z.string().trim().min(1).optional(),
});
export const proposeArtifactRevisionBodySchema = z.object({
    title: z.string().trim().min(1).optional(),
    markdown: z.string().min(1),
    baseVersionId: z.string().trim().min(1).optional(),
});
export const artifactRevisionActionBodySchema = z.object({
    versionId: z.string().trim().min(1),
});
export const listTaskArtifactsParamsSchema = z.object({
    taskId: z.string().trim().min(1),
});
export const listRunArtifactsParamsSchema = z.object({
    runId: z.string().trim().min(1),
});
export const listSessionArtifactsParamsSchema = z.object({
    sessionId: z.string().trim().min(1),
});
